import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { admin as api } from "@/lib/api/admin";
import type {
  AdminNotification,
  AdminNotificationInput,
} from "@/lib/api/admin";
import { useAuth } from "./useAuth";

// Re-export so admin components can pull these from the hook layer
// (no-restricted-imports).
export type {
  AdminNotification,
  AdminNotificationInput,
  NotificationTargeting,
} from "@/lib/api/admin";

export type AdminNotificationsStatus = "idle" | "loading" | "ready" | "error";

export interface AdminNotificationsContextValue {
  status: AdminNotificationsStatus;
  notifications: readonly AdminNotification[];
  error: Error | null;
  reload: () => Promise<void>;
  create: (input: AdminNotificationInput) => Promise<AdminNotification>;
  remove: (id: string) => Promise<void>;
}

const AdminNotificationsContext = createContext<AdminNotificationsContextValue>(
  null!,
);

export function useAdminNotifications() {
  return useContext(AdminNotificationsContext);
}

function useAdminNotificationsProvider(): AdminNotificationsContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<AdminNotificationsStatus>("idle");
  const [items, setItems] = useState<readonly AdminNotification[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    () =>
      api
        .listAdminNotifications()
        .then(({ notifications }) => {
          setItems(notifications);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const create = useCallback(async (input: AdminNotificationInput) => {
    const { notification } = await api.createAdminNotification(input);
    setItems((prev) => [notification, ...prev]);
    return notification;
  }, []);

  const remove = useCallback(async (id: string) => {
    await api.deleteAdminNotification(id);
    setItems((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return useMemo(
    () => ({ status, notifications: items, error, reload, create, remove }),
    [status, items, error, reload, create, remove],
  );
}

export function AdminNotificationsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useAdminNotificationsProvider();
  return (
    <AdminNotificationsContext.Provider value={value}>
      {children}
    </AdminNotificationsContext.Provider>
  );
}
